// @flow
import React, { PureComponent } from 'react';
import { pick } from 'lodash';

import type { Status } from 'types/Status';

export type StateProps = {
  placeholder: string,
  status: Status,
};

type OwnProps = {
  illustration: string,
  srcset: Object,
  forceState: ?'thumbnail' | 'medium' | 'medium_large' | 'large',
  baliseImg?: boolean,
  itemProp?: string,
  bypassClubPlaceholder?: boolean,
};

type State = {
  lockState: boolean,
};

type SourceType = {
  type: string,
  src: string,
  width: number,
  height: number,
};

type Props = OwnProps & StateProps;

class BackgroundImage extends PureComponent<Props, State> {
  htmlElement: ?HTMLElement;
  OwnProps: OwnProps;
  state: State = {
    lockState: false,
  };

  static defaultProps = {
    srcset: {
      type: 'default',
      illustration: '',
      width: 0,
      height: 0,
    },
    forceState: null,
    bypassClubPlaceholder: false,
  };

  get isLocked() {
    const { lockState } = this.state;
    return lockState;
  }

  componentDidMount() {
    this.updateBackgroundSource(this.getLightSource());
    this.lazyLoad(this.getTargetSource());
  }

  componentDidUpdate(prevProps: Props) {
    const { srcset, illustration, status } = this.props;

    if (
      (prevProps.srcset.type === 'default' && srcset.type !== 'default') ||
      prevProps.status !== status
    ) {
      this.updateBackgroundSource(this.getBetterSource());
    }

    if (prevProps.illustration !== illustration) {
      this.lazyLoad(this.getTargetSource());
    }
  }

  getFallback(): SourceType {
    const { illustration, placeholder, bypassClubPlaceholder } = this.props;

    const src =
      (!bypassClubPlaceholder
      && (!illustration || illustration.includes('/img/placeholder/placeholder-ffr.jpg'))
      && placeholder)
      || illustration;

    return {
      type: 'default',
      src,
      width: 0,
      height: 0,
    };
  }

  getLightSource() {
    const { srcset } = this.props;
    if (srcset && srcset.thumbnail) {
      return {
        type: 'thumbnail',
        ...srcset.thumbnail
      };
    }

    return this.getFallback();
  }

  getTargetSource() {
    const { forceState, srcset } = this.props;

    if (!forceState) {
      return this.getBetterSource();
    }

    if (srcset && srcset.hasOwnProperty(forceState)) {
      return {
        type: forceState,
        ...srcset[forceState],
      };
    }

    return this.getFallback();
  }

  getBetterSource() {
    const { srcset } = this.props;
    if (srcset && srcset.medium_large) {
      return {
        type: 'medium_large',
        ...srcset.medium_large,
      };
    }

    if (srcset && srcset.medium) {
      return {
        type: 'medium',
        ...srcset.medium,
      };
    }

    return this.getFallback();
  }

  lazyLoad(source: SourceType): void {
    const { src, width, height } = source;
    const image = new Image();
    image.onload = (e: Event) => this.updateBackgroundSource(source);

    image.src = src || '';
    width && (image.width = width);
    height && (image.height = height);
  }

  updateBackgroundSource(source: SourceType): void {
    if (!this.htmlElement || !source) {
      return;
    }

    this.htmlElement.style.backgroundImage = `url("${source.src}")`;
    this.htmlElement.setAttribute('data-background-type', source.type);
  }

  render() {
    const props = pick(this.props, [
      'className',
      'placeholder'
    ]);
    const { itemProp, baliseImg, illustration } = this.props;

    return (
      <div
        ref={ref => this.htmlElement = ref}
        {...props} >
        {baliseImg && <img src={illustration} itemProp={itemProp} alt=""/>}
      </div>
    );
  }

}

export default BackgroundImage;
